//文字コードはutf-8

/* お問い合わせCTAの Lottie アニメーション
 *  - 通常時：キャラクターたちが力を合わせて手紙を持ち上げようと踏ん張る（わっしょい）ループ [16–24]
 *  - ホバー/フォーカス時：手紙が浮いて少しペラッとめくれ、キャラクターが喜ぶ [24–40]
 *  - リーブ時：逆再生で通常のループへ戻る
 *  - JSON が約340KBあり、CTAはページ最下部にあるため、画面内に入り読み込みが
 *    落ち着いてから取得する（kvLottie.js と同じ方針。それまではフォールバック画像を表示）
 *  Lottie ランタイムは lib/lottie.min.js（グローバル window.lottie）を使用
========================================================*/

// フレーム定義（fr=8 / コンポジション 16–40）
const FRAME = {
    idleIn: 16,    // 持ち上げ（わっしょい）ループ開始
    idleOut: 24,   // 持ち上げループ終端 ＝ 手紙が浮き始めるフェーズ境界
    revealOut: 40, // 手紙が浮く〜キャラ歓喜の終端
};

// ページの読み込み完了後に実行する（本文の表示を JSON 取得で妨げないため）
function afterLoad(callback) {
    if (document.readyState === 'complete') {
        callback();
        return;
    }
    window.addEventListener('load', callback, { once: true });
}

export function initContactLottie() {
    const root = document.querySelector('.jsContactLottie');
    if (!root) return;

    const lottie = window.lottie;
    // ランタイム未読込 or JSONパス未指定ならフォールバック画像のまま
    if (!lottie || !root.dataset.lottie) return;

    // モーション低減設定時はアニメーションさせず、静止画（フォールバック）を維持
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // リンクカード全体をホバー領域に（矢印演出と同じ挙動）
    const trigger = root.closest('a') || root;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    let anim = null;

    // サブフレーム補間を無効化してコマ送り（カクカク）で再生する。
    // lottie-web は既定で rAF ごとにキーフレーム間を補間するため、コンポジションが
    // 8fps でも見た目は滑らかになってしまう。false にすると描画フレームが整数へ
    // 丸められ（gotoFrame の ~~currentRawFrame）、contact.json の fr:8 がそのまま
    // 8fps のコマ送りとして出る。
    const SUBFRAME = false;

    let ready = false;
    let hovered = false;
    let inReveal = false; // 手紙が浮く区間 [24,40] が有効かどうか

    // 通常時：わっしょいループ [16,24]
    const idleLoop = () => {
        inReveal = false;
        anim.loop = true;
        anim.setDirection(1);
        anim.playSegments([FRAME.idleIn, FRAME.idleOut], true);
    };
    // 手紙区間 [24,40] の展開を開始。idle の1周が 24 まで終わってから呼ぶので、
    // 24＝24 で位置が一致し、ジャンプせずシームレスに繋がる（封筒が閉じた状態から浮上）。
    const startReveal = () => {
        inReveal = true;
        anim.loop = false;
        anim.setDirection(1);
        anim.playSegments([FRAME.idleOut, FRAME.revealOut], true);
    };
    // ホバー時：いま進行中のループ1周を最後（24）まで再生させてから展開へ繋ぐ。
    // 途中でぶつ切りにせず動きを完結させることで、ホバー後の浮上へ滑らかに連結する。
    const reveal = () => {
        if (!ready || hovered) return;
        hovered = true;
        anim.setDirection(1);
        if (inReveal) {
            // すでに [24,40] 区間内（リーブ逆再生中の再ホバー等）→ 現在位置から順走
            anim.loop = false;
            anim.play();
        } else {
            // 通常ループ中 → ループだけ解除。現在の1周が 24 に到達すると complete が発火し、
            // そこで startReveal() が展開区間へ接続する（下の complete ハンドラ参照）。
            anim.loop = false;
        }
    };
    // リーブ時：現在フレームからそのまま逆再生（24へ）。完了後に通常ループへ戻す。
    // 区間 [24,40] が有効なので play() だけで区間内を逆走し 24 で止まる。
    // playSegments(forceFlag) を使うと 40（封筒が開いた状態）へ一瞬ジャンプしてしまうため使わない。
    const unreveal = () => {
        if (!ready || !hovered) return;
        hovered = false;
        if (inReveal) {
            // 展開中/展開済み → 現在位置から逆再生で閉じる
            anim.loop = false;
            anim.setDirection(-1);
            anim.play();
        } else {
            // まだ通常ループの1周を消化中（展開未開始）→ ループを再開するだけ
            anim.loop = true;
        }
    };

    // 読込完了時点で既にポインタ/フォーカスが乗っているか
    const activeOnLoad = () =>
        (canHover && trigger.matches(':hover')) || trigger.contains(document.activeElement);

    // Lottie本体の生成。画面内に入ってから1度だけ呼ぶ
    const build = () => {
        if (anim) return;
        anim = lottie.loadAnimation({
            container: root,
            renderer: 'svg',
            loop: false,
            autoplay: false,
            path: root.dataset.lottie,
            rendererSettings: { progressiveLoad: true },
        });
        anim.setSubframe(SUBFRAME);

        anim.addEventListener('DOMLoaded', () => {
            ready = true;
            root.classList.add('is-lottieReady'); // フォールバック画像を隠す
            idleLoop();
            if (activeOnLoad()) reveal();
        });

        // ループ以外の再生が完了したとき
        anim.addEventListener('complete', () => {
            if (hovered && !inReveal) {
                // 通常ループの1周が 24 で完了 → 手紙区間へシームレスに接続
                startReveal();
            } else if (!hovered) {
                // 逆再生が 24 で完了 → 通常ループへ復帰
                idleLoop();
            }
            // hovered && inReveal：フレーム40で展開完了 → そのまま静止保持（何もしない）
        });
    };

    // ホバー可能な実ポインタのみ（タッチのホバー残り防止）。フォーカスはキーボード対応で常時。
    if (canHover) {
        trigger.addEventListener('pointerenter', reveal);
        trigger.addEventListener('pointerleave', unreveal);
    }
    trigger.addEventListener('focusin', reveal);
    trigger.addEventListener('focusout', unreveal);

    // 初回は画面内に入ってから生成（JSON取得を遅延）、以降は画面外で停止して負荷軽減
    // （ホバー演出中は触らない）
    if (!('IntersectionObserver' in window)) {
        afterLoad(build);
        return;
    }

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                if (!anim) {
                    afterLoad(build); // 初回はここで読み込む
                } else if (!hovered) {
                    anim.play();      // 通常ループを再開
                }
            } else if (anim && !hovered) {
                anim.pause();         // 画面外は停止
            }
        });
    }, { threshold: 0 });
    io.observe(root);
}
